/**
 * Lightweight default toast for a failed API call.
 *
 * Error handling for API calls is the caller's responsibility: a caller
 * `catch`es the rejection, deals with any error it wants to handle with
 * contextually appropriate feedback, and passes anything else here for a
 * generic toast. Takes the `Datastore` (it reads `AppData` off it) and accepts
 * `error: unknown` so callers don't repeat the type narrowing:
 *
 * - **ORPCError** — server-shaped failure; toast uses its code and message.
 * - **Error** — transport / decode failure (fetch threw, body malformed). No
 *   oRPC code, so it falls into a generic `NETWORK` bucket, using its message.
 * - **anything else** — stringified under the `NETWORK` bucket.
 *
 * The dedupe id is keyed on the error code so repeated failures of the same
 * kind (e.g. a query the caller retries) collapse into one visible toast
 * (`addNotificationToast` ignores duplicate ids).
 */
import { ORPCError } from '@orpc/client';
import { addNotificationToast } from 'component/notification-toast';
import type { Datastore } from 'util/datastore';

export function showApiErrorToast({ datastore, error }: { datastore: Datastore; error: unknown }) {
    const code = error instanceof ORPCError ? error.code : 'NETWORK';
    const message = error instanceof Error ? error.message : String(error);
    addNotificationToast({
        appData: datastore.getAppData(),
        id: `api_error_${code}`,
        type: 'error',
        duration: 7000,
        // Wrap as a runtime TLabel rather than going through `t()` which
        // rejects non-literal defaultText.
        label: { key: null, defaultText: message },
        dismissable: true,
    });
}
